/**
 * Everything native, behind one interface. Nothing above src/platform/ may
 * import from `@tauri-apps/api` — see the ESLint rule in eslint.config.js.
 *
 * The implementation is chosen once at startup and passed via context, not
 * imported as a singleton.
 */
export interface Platform {
  fetchUrl(url: string): Promise<{ status: number; html: string; finalUrl: string }>;
  /**
   * `referer` because a lot of retail CDNs return 403 to requests without one.
   * Pass the product page URL.
   *
   * The Blob comes back untyped — a CDN's Content-Type is not reliable enough
   * to store, so the format is sniffed from the bytes in `import/mime.ts`.
   */
  fetchBinary(url: string, referer?: string): Promise<Blob>;
  /**
   * Offers `name` in a native save dialog and writes `data` where the user
   * puts it. Resolves false if they dismissed the dialog — cancelling a
   * backup is a normal thing to do, not an error to report.
   */
  saveFile(name: string, data: Blob): Promise<boolean>;
  /**
   * Opens the folder the database lives in, in the system file manager. Not
   * the install folder — the two are unrelated, and that is exactly what this
   * makes visible.
   *
   * Takes no path. The caller doesn't know where it is and shouldn't: a folder
   * name crossing this boundary and reaching the shell is how a stored string
   * becomes an execution.
   *
   * Optional, because a browser has no folder to open and no file manager to
   * open it with. Absent means the button is not drawn — check before calling.
   * A capability that can't exist is better modelled as missing than as one
   * that always throws.
   */
  showDataFolder?(): Promise<void>;
  /** Hands a URL to the system browser. The item panel's source link. */
  openExternal(url: string): Promise<void>;
}

/** Thrown by a platform capability that this build cannot provide. */
export class PlatformUnavailableError extends Error {
  readonly capability: keyof Platform;

  constructor(capability: keyof Platform, reason: string) {
    super(`${capability}() is unavailable: ${reason}`);
    this.name = 'PlatformUnavailableError';
    this.capability = capability;
  }
}
